/**
 * HeadlessOutliner.tsx - OpCentral Outliner using @headless-tree/react
 * 
 * A fully extensible outliner component that integrates with OpCentral orchestrators.
 * Uses headless-tree for maximum customization and control.
 */

import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useTree } from '@headless-tree/react';
import {
  syncDataLoaderFeature,
  selectionFeature,
  hotkeysCoreFeature,
  dragAndDropFeature,
  renamingFeature,
  searchFeature,
} from '@headless-tree/core';
import './HeadlessOutliner.css';

interface OutlinerItem {
  id: string;
  name: string;
  title?: string;
  parentId: string | null;
  children?: string[];
  isExpanded?: boolean;
  [key: string]: any;
}

interface HeadlessOutlinerProps {
  data: OutlinerItem[];
  onDataChange?: (data: OutlinerItem[]) => void;
  
  // OpCentral Orchestrators
  actr?: any;
  aggregatr?: any;
  interfacr?: any;
  profylr?: any;
  backtrackr?: any;
  
  // Workspace context
  workspaceId?: string;
  datasetId?: string;
  
  // Configuration
  config?: {
    indentSize?: number;
    showIcons?: boolean;
    enableDragDrop?: boolean;
    enableSearch?: boolean;
    enableRename?: boolean;
  };
}

export const HeadlessOutliner: React.FC<HeadlessOutlinerProps> = ({
  data,
  onDataChange,
  actr,
  aggregatr,
  interfacr,
  profylr,
  backtrackr,
  workspaceId,
  datasetId,
  config = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  
  // Force refresh when data changes
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [data.length]);
  
  const {
    indentSize = 24,
    showIcons = true,
    enableDragDrop = true,
    enableSearch = true,
    enableRename = true,
  } = config;

  // Convert flat data to tree structure for headless-tree
  const treeData = useMemo(() => {
    const itemMap = new Map<string, OutlinerItem>();
    data.forEach(item => itemMap.set(item.id, item));
    
    return {
      rootItemId: 'root',
      items: itemMap,
    };
  }, [data]);

  // Data loader for headless-tree
  const dataLoader = useMemo(() => ({
    getItem: (itemId: string) => {
      if (itemId === 'root') {
        return {
          id: 'root',
          name: 'Root',
          parentId: null,
          children: data.filter(item => !item.parentId).map(item => item.id),
        };
      }
      return data.find(item => item.id === itemId);
    },
    getChildren: (itemId: string) => {
      if (itemId === 'root') {
        return data.filter(item => !item.parentId).map(item => item.id);
      }
      return data.filter(item => item.parentId === itemId).map(item => item.id);
    },
  }), [data]);

  // Handle item creation via Actr
  const handleCreateItem = useCallback(async (parentId: string | null, itemData: Partial<OutlinerItem>) => {
    if (!actr) return null;
    
    const result = await actr.execute('outliner:add-child', {
      parentId: parentId === 'root' ? null : parentId,
      data: itemData,
    });
    
    return result?.id;
  }, [actr]);

  // Handle item update via Actr
  const handleUpdateItem = useCallback(async (itemId: string, updates: Partial<OutlinerItem>) => {
    if (!actr) return;
    
    await actr.execute('outliner:update', {
      itemId,
      updates,
    });
  }, [actr]);

  // Handle item delete via Actr
  const handleDeleteItem = useCallback(async (itemId: string) => {
    if (!actr) return;
    
    await actr.execute('outliner:delete', {
      itemId,
    });
  }, [actr]);

  // Handle item move via Actr
  const handleMoveItem = useCallback(async (itemId: string, newParentId: string | null, newIndex: number) => {
    if (!actr) return;
    
    await actr.execute('outliner:move', {
      itemId,
      toParentId: newParentId === 'root' ? null : newParentId,
      toIndex: newIndex,
    });
  }, [actr]);

  // Build features list
  const features = useMemo(() => {
    const featureList = [
      syncDataLoaderFeature,
      selectionFeature,
      hotkeysCoreFeature,
    ];
    
    if (enableDragDrop) {
      featureList.push(dragAndDropFeature);
    }
    
    if (enableRename) {
      featureList.push(renamingFeature);
    }
    
    if (enableSearch) {
      featureList.push(searchFeature);
    }
    
    return featureList;
  }, [enableDragDrop, enableRename, enableSearch]);

  // Tree key to force remount when data changes
  const treeKey = `tree-${refreshKey}`;
  
  const tree = useTree({
    rootItemId: 'root',
    getItemName: (item) => item.getItemData()?.name || item.getItemData()?.title || 'Untitled',
    isItemFolder: (item) => {
      const itemData = item.getItemData();
      if (!itemData) return false;
      const children = data.filter(i => i.parentId === itemData.id);
      return children.length > 0;
    },
    dataLoader,
    features,
    onRename: async (item, newName) => {
      await handleUpdateItem(item.getId(), { name: newName });
    },
    onDrop: async (items, target) => {
      for (const item of items) {
        const targetIndex = 'childIndex' in target ? target.childIndex : 0;
        await handleMoveItem(
          item.getId(),
          target.item.getId(),
          targetIndex
        );
      }
    },
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      
      // Get selected items
      const selectedItems = tree.getSelectedItems();
      if (selectedItems.length === 0) return;
      
      const lastSelected = selectedItems[selectedItems.length - 1];
      const lastId = lastSelected.getId();
      
      // Enter: Add sibling (simple)
      if (e.key === 'Enter' && !cmdOrCtrl && !e.shiftKey) {
        e.preventDefault();
        const parentId = lastSelected.getItemData()?.parentId || null;
        const newId = await handleCreateItem(parentId, { name: 'New Item' });
        if (newId) {
          // Select and focus new item
          setTimeout(() => {
            const newItem = tree.getItems().find(item => item.getId() === newId);
            if (newItem) {
              newItem.select();
              newItem.startRenaming();
            }
          }, 100);
        }
      }
      
      // Cmd+Enter: Add sibling
      else if (e.key === 'Enter' && cmdOrCtrl && !e.shiftKey) {
        e.preventDefault();
        const parentId = lastSelected.getItemData()?.parentId || null;
        const newId = await handleCreateItem(parentId, { name: 'New Item' });
        if (newId) {
          setTimeout(() => {
            const newItem = tree.getItems().find(item => item.getId() === newId);
            if (newItem) {
              newItem.select();
              newItem.startRenaming();
            }
          }, 100);
        }
      }
      
      // Shift+Enter: Add child
      else if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        const newId = await handleCreateItem(lastId, { name: 'New Item' });
        if (newId) {
          setTimeout(() => {
            const newItem = tree.getItems().find(item => item.getId() === newId);
            if (newItem) {
              newItem.select();
              newItem.startRenaming();
            }
          }, 100);
        }
      }
      
      // Delete/Backspace: Delete item
      else if ((e.key === 'Delete' || e.key === 'Backspace') && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        for (const item of selectedItems) {
          await handleDeleteItem(item.getId());
        }
      }
      
      // Cmd+Z: Undo
      else if (e.key === 'z' && cmdOrCtrl && !e.shiftKey && backtrackr) {
        e.preventDefault();
        console.log('[HeadlessOutliner] Undo requested');
        await backtrackr.undo();
      }
      
      // Cmd+Shift+Z: Redo
      else if (e.key === 'z' && cmdOrCtrl && e.shiftKey && backtrackr) {
        e.preventDefault();
        console.log('[HeadlessOutliner] Redo requested');
        await backtrackr.redo();
      }
      
      // Cmd+C: Copy (via Clippr if available)
      else if (e.key === 'c' && cmdOrCtrl && interfacr?.clippr) {
        e.preventDefault();
        const selectedIds = selectedItems.map(item => item.getId());
        const selectedData = data.filter(item => selectedIds.includes(item.id));
        await interfacr.clippr.copy(selectedData);
        console.log('[HeadlessOutliner] Copied', selectedData.length, 'items');
      }
      
      // Cmd+X: Cut (via Clippr if available)
      else if (e.key === 'x' && cmdOrCtrl && interfacr?.clippr) {
        e.preventDefault();
        const selectedIds = selectedItems.map(item => item.getId());
        const selectedData = data.filter(item => selectedIds.includes(item.id));
        await interfacr.clippr.cut(selectedData);
        console.log('[HeadlessOutliner] Cut', selectedData.length, 'items');
      }
      
      // Cmd+V: Paste (via Clippr if available)
      else if (e.key === 'v' && cmdOrCtrl && interfacr?.clippr) {
        e.preventDefault();
        const clipboardData = await interfacr.clippr.paste();
        if (clipboardData && Array.isArray(clipboardData)) {
          for (const item of clipboardData) {
            await handleCreateItem(lastId, item);
          }
          console.log('[HeadlessOutliner] Pasted', clipboardData.length, 'items');
        }
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [tree, handleCreateItem, handleDeleteItem, backtrackr]);

  // Render tree items
  const items = tree.getItems();

  return (
    <div 
      key={treeKey}
      ref={(el) => {
        if (el) {
          (containerRef as any).current = el;
          tree.registerElement(el);
        }
      }}
      className="headless-outliner"
      tabIndex={0}
    >
      {items.map((item) => {
        const itemData = item.getItemData();
        if (!itemData || itemData.id === 'root') return null;
        
        const level = item.getItemMeta().level;
        const isSelected = item.isSelected();
        const isFolder = item.isFolder();
        const isExpanded = item.isExpanded();
        const isRenaming = item.isRenaming();
        
        return (
          <div
            key={item.getId()}
            className={`outliner-row ${isSelected ? 'selected' : ''}`}
            style={{
              paddingLeft: `${level * indentSize}px`,
            }}
          >
            {/* Disclosure triangle */}
            {isFolder && (
              <button
                className="disclosure-triangle"
                onClick={() => isExpanded ? item.collapse() : item.expand()}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            
            {/* Item content */}
            {isRenaming ? (
              <input
                className="rename-input"
                defaultValue={item.getItemName()}
                onBlur={() => {/* Renaming handled by feature */}}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.currentTarget.blur();
                  }
                }}
                autoFocus
              />
            ) : (
              <div
                {...item.getProps()}
                ref={item.registerElement}
                className="item-content"
                onClick={(e) => {
                  // Single click on text to select
                  if (e.detail === 1) {
                    item.select();
                  }
                  // Double click on text to rename
                  else if (e.detail === 2) {
                    item.startRenaming();
                  }
                }}
              >
                {showIcons && (
                  <span className="item-icon">
                    {isFolder ? '📁' : '📄'}
                  </span>
                )}
                <span className="item-name">
                  {item.getItemName()}
                </span>
              </div>
            )}
          </div>
        );
      })}
      
      {items.length === 0 && (
        <div className="empty-state">
          <p>No items yet. Press Cmd+Enter to create one.</p>
        </div>
      )}
    </div>
  );
};

export default HeadlessOutliner;
